<template>
  <MainLayout>

    <!-- -- Hero ----------------------------------------------------------------- -->
    <section class="relative bg-gradient-hero py-7 lg:py-10 overflow-hidden">
      <div class="absolute inset-0 hex-pattern pointer-events-none"></div>
      <div class="absolute right-1/3 top-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <!-- EKG pulse decoration -->
      <svg class="absolute bottom-0 left-0 w-full opacity-10 pointer-events-none" height="60" viewBox="0 0 1200 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <polyline points="0,30 200,30 240,30 260,5 280,55 300,15 320,45 340,30 600,30 640,30 660,8 680,52 700,18 720,42 740,30 1200,30" stroke="#9AB4C8" stroke-width="2" fill="none"/>
      </svg>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-4 animate-fade-up" style="animation-delay:0.1s">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">What We Offer</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h1 class="text-5xl sm:text-6xl font-black text-white mb-4 animate-fade-up" style="animation-delay:0.2s">
          Our <span class="text-secondary">Services</span>
        </h1>
        <p class="text-white/60 text-base max-w-xl mx-auto mb-6 animate-fade-up" style="animation-delay:0.3s">
          Comprehensive biomedical and healthcare solutions delivered by experienced engineers and specialists.
        </p>
        <nav class="flex items-center justify-center gap-2 text-sm text-white/50 animate-fade-up" style="animation-delay:0.4s">
          <RouterLink to="/" class="hover:text-secondary transition-colors duration-200">Home</RouterLink>
          <i class="fa-solid fa-chevron-right text-xs text-white/30"></i>
          <span class="text-white/80">Our Services</span>
        </nav>
      </div>
    </section>

    <!-- -- Service Intro -------------------------------------------------------- -->
    <section class="py-20 lg:py-24 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-16 items-center">

          <!-- Left: Feature image -->
          <div class="relative" data-aos="fade-right">
            <div class="absolute -top-6 -left-6 w-48 h-48 bg-secondary/10 rounded-3xl -z-10"></div>
            <div class="absolute -bottom-6 -right-6 w-28 h-28 bg-primary/8 rounded-2xl -z-10"></div>
            <div class="relative rounded-3xl overflow-hidden shadow-card-lg">
              <img
                :src="serviceImg"
                alt="Orel Medical Services"
                class="w-full h-auto object-cover"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-primary-darkest/30 to-transparent pointer-events-none"></div>
            </div>
            <!-- Quick list overlay card -->
            <div class="absolute -bottom-5 -right-5 bg-white rounded-2xl p-5 shadow-card-lg max-w-[220px]">
              <h5 class="text-xs font-black text-primary uppercase tracking-wider mb-3">Services Offered</h5>
              <ul class="space-y-2">
                <li v-for="svc in servicesList" :key="svc" class="flex items-center gap-2 text-xs text-gray-600">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-secondary flex-shrink-0">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {{ svc }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Right: Intro content -->
          <div data-aos="fade-left" data-aos-delay="150">
            <div class="section-label">Our Services</div>
            <h2 class="section-title mb-5">
              Your Partners in Healthcare Excellence - Setting the Standard
            </h2>
            <p class="text-gray-500 leading-relaxed mb-5 text-justify">
              Our dedicated teams craft tailored healthcare solutions aligned with your facility's vision and operational demands. With Orel's support, you'll improve patient outcomes, enhance operational efficiency, and build lasting supplier partnerships built on trust and compliance.
            </p>
            <p class="text-gray-500 leading-relaxed mb-6 text-justify">
              From biomedical equipment training to tender document preparation and electrical installations - we provide the full spectrum of services your healthcare facility needs to operate at its best.
            </p>

            <!-- Feature badges -->
            <div class="grid grid-cols-2 gap-3 mb-8">
              <div v-for="feat in features" :key="feat.label" class="flex items-center gap-2.5 bg-primary/5 rounded-xl p-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-primary flex-shrink-0" v-html="feat.svgPath"></svg>
                <span class="text-gray-700 text-xs font-medium">{{ feat.label }}</span>
              </div>
            </div>

            <RouterLink to="/contact-us" class="btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              Get in Touch
            </RouterLink>
          </div>

        </div>
      </div>
    </section>

    <!-- -- Service Cards -------------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-[#f7f5fb] overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div class="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">What We Provide</div>
          </div>
          <h2 class="section-title">Comprehensive Healthcare Solutions</h2>
          <p class="section-subtitle">Purpose-built services for hospitals, clinics, NGOs, and government health facilities across Uganda.</p>
        </div>

        <!-- Service cards -->
        <div class="space-y-6">
          <div
            v-for="(service, index) in services"
            :key="service.title"
            class="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-lg transition-all duration-300 grid lg:grid-cols-12 gap-0"
            data-aos="fade-up"
            :data-aos-delay="index * 100"
          >
            <!-- Icon column -->
            <div class="lg:col-span-2 bg-gradient-primary flex items-center justify-center py-10 lg:py-0 min-h-[120px] relative overflow-hidden">
              <div class="absolute inset-0 dot-pattern opacity-20 pointer-events-none"></div>
              <div class="relative text-center">
                <div class="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center mx-auto">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-white" v-html="service.svgPath"></svg>
                </div>
              </div>
            </div>
            <!-- Content column -->
            <div class="lg:col-span-10 p-7 lg:p-9 flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div class="flex-1">
                <span class="text-[10px] font-black text-primary uppercase tracking-widest bg-primary/8 px-3 py-1 rounded-full">
                  Service {{ String(index + 1).padStart(2, '0') }}
                </span>
                <h3 class="font-black text-gray-900 text-xl mt-3 mb-3">{{ service.title }}</h3>
                <p class="text-gray-500 text-sm leading-relaxed text-justify mb-4">{{ service.description }}</p>
                <!-- Bullet highlights -->
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="point in service.points"
                    :key="point"
                    class="inline-flex items-center gap-1.5 text-xs text-primary bg-primary/8 px-2.5 py-1 rounded-full"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {{ point }}
                  </span>
                </div>
              </div>
              <RouterLink
                to="/contact-us"
                class="inline-flex items-center gap-2 text-primary text-sm font-bold hover:text-white hover:bg-primary px-5 py-2.5 rounded-xl border-2 border-primary/20 hover:border-primary transition-all duration-300 flex-shrink-0 whitespace-nowrap"
              >
                Enquire
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- -- Why Our Services ----------------------------------------------------- -->
    <section class="py-20 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-20 items-center">

          <!-- Left: content -->
          <div data-aos="fade-right">
            <div class="section-label">Why Orel</div>
            <h2 class="section-title mb-6">
              Backed by Expertise, Driven by Results
            </h2>
            <p class="text-gray-500 leading-relaxed mb-8 text-justify">
              With over 12 years of collective biomedical engineering expertise, Orel Medical Supplies brings unmatched technical depth and healthcare sector knowledge to every engagement. We do not just supply - we partner, advise, install, train, and support.
            </p>
            <div class="space-y-4">
              <div v-for="benefit in whyUs" :key="benefit.title" class="flex items-start gap-4">
                <div class="w-10 h-10 bg-primary/8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-primary" v-html="benefit.svgPath"></svg>
                </div>
                <div>
                  <h4 class="font-bold text-gray-900 text-sm mb-0.5">{{ benefit.title }}</h4>
                  <p class="text-gray-500 text-sm leading-relaxed">{{ benefit.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Image mosaic -->
          <div class="grid grid-cols-2 gap-4" data-aos="fade-left" data-aos-delay="150">
            <img :src="galleryImgs[0]" alt="Healthcare services" class="rounded-2xl object-cover h-56 w-full shadow-card" loading="lazy"/>
            <img :src="galleryImgs[1]" alt="Biomedical equipment" class="rounded-2xl object-cover h-56 w-full shadow-card mt-8" loading="lazy"/>
            <img :src="galleryImgs[2]" alt="Medical training" class="rounded-2xl object-cover h-56 w-full shadow-card -mt-4" loading="lazy"/>
            <img :src="galleryImgs[3]" alt="Equipment installation" class="rounded-2xl object-cover h-56 w-full shadow-card mt-4" loading="lazy"/>
          </div>

        </div>
      </div>
    </section>

    <!-- -- CTA ------------------------------------------------------------------ -->
    <section class="relative py-20 bg-gradient-cta overflow-hidden">
      <div class="absolute inset-0 hex-pattern opacity-20 pointer-events-none"></div>
      <!-- EKG pulse line -->
      <svg class="absolute top-0 left-0 w-full opacity-15 pointer-events-none" height="40" viewBox="0 0 1200 40" fill="none" preserveAspectRatio="none">
        <polyline points="0,20 300,20 340,20 360,3 380,37 400,10 420,30 440,20 900,20 940,20 960,5 980,35 1000,12 1020,28 1040,20 1200,20" stroke="#9AB4C8" stroke-width="1.5" fill="none"/>
      </svg>
      <div class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl sm:text-4xl font-black text-white mb-4" data-aos="fade-up">
          Ready to Transform Your Healthcare Facility?
        </h2>
        <p class="text-white/65 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="100">
          Contact us today to learn how our services can elevate your healthcare delivery and operational efficiency.
        </p>
        <RouterLink to="/contact-us" class="btn-white" data-aos="fade-up" data-aos-delay="200">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
          Get a Quote Today
        </RouterLink>
      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import MainLayout from './Layout/MainLayout.vue'

const serviceImg  = require('../assets/orel-imgs/health.png')
const galleryImgs = [
  require('../assets/orel-imgs/image-004.jpg'),
  require('../assets/orel-imgs/image-010.jpg'),
  require('../assets/orel-imgs/image-016.jpg'),
  require('../assets/orel-imgs/image-022.jpg'),
]

const servicesList = [
  'Trainings',
  'Tender Document Preparations',
  'Electrical Installations',
  'Biomedical Consultancy',
]

const features = [
  { label: 'Expert Team',         svgPath: '<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>' },
  { label: 'Quality Certified',   svgPath: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>' },
  { label: 'Timely Delivery',     svgPath: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' },
  { label: 'After-Sales Support', svgPath: '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.64A2 2 0 012 .17h3a2 2 0 012 1.72 12.05 12.05 0 00.72 2.92 2 2 0 01-.45 2.11l-1.27 1.27a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.05 12.05 0 002.92.72A2 2 0 0122 13.92v3z"/>' },
  { label: 'Compliance Assured',  svgPath: '<path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>' },
  { label: 'Tailored Solutions',  svgPath: '<circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>' },
]

const services = [
  {
    svgPath:     '<path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>',
    title:       'Trainings',
    description: 'Professional education in healthcare equipment operation, infection control, and technology integration. We equip healthcare workers with the competencies to operate medical equipment safely, maintain infection control standards, and seamlessly integrate new technologies into clinical workflows.',
    points:      ['Equipment Operation', 'Infection Control', 'Technology Integration', 'Hands-On Sessions'],
  },
  {
    svgPath:     '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
    title:       'Tender Document Preparations',
    description: 'Streamlined medical procurement support, ensuring full compliance with regulatory standards and institutional requirements. Our team prepares comprehensive, audit-ready tender documents covering technical specifications, delivery schedules, warranty terms, and compliance certifications.',
    points:      ['Technical Specifications', 'Regulatory Compliance', 'Budget Alignment', 'Procurement Strategy'],
  },
  {
    svgPath:     '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
    title:       'Electrical Installations',
    description: 'Safe, efficient medical equipment electrical installation for healthcare facilities of all sizes. Our certified technicians manage the complete installation lifecycle - from load assessment and circuit planning through equipment commissioning - ensuring every installation meets the highest safety and operational standards.',
    points:      ['Load Assessment', 'Safe Commissioning', 'Safety Compliance', 'Post-Install Testing'],
  },
  {
    svgPath:     '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
    title:       'Biomedical Consultancy',
    description: 'Expert strategic guidance in health facility design, biomedical service optimization, and East African healthcare market entry. We partner with hospitals, NGOs, and investors to plan equipment procurement, design biomedical workflows, and navigate the regulatory landscape - ensuring your facility is equipped to deliver world-class care.',
    points:      ['Facility Planning', 'Equipment Selection', 'Market Entry Support', 'Workflow Optimization'],
  },
]

const whyUs = [
  {
    title:   'Certified Biomedical Engineers',
    desc:    'Our team holds recognized biomedical engineering qualifications with collective expertise spanning more than 12 years in Uganda\'s healthcare sector.',
    svgPath: '<path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
  },
  {
    title:   'Regulatory and Compliance Expertise',
    desc:    'We ensure every service and product meets WHO guidelines, national health authority standards, and institutional procurement requirements.',
    svgPath: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
  },
  {
    title:   'End-to-End Service Support',
    desc:    'From initial consultation through installation, training, and maintenance - we provide seamless support at every stage of your facility\'s journey.',
    svgPath: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  },
]
</script>
